/* eslint-disable react/prop-types */
import {useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import AdDisplay from '@adv-mt/mt-ad-display'
import Layout from '@adv-mt/mt-layout-basic'
import ListMarketedVersions from '@adv-mt/mt-list-marketed-versions'
import ListModelDetails from '@adv-mt/mt-list-model-details'
import MotorTagCloud from '@adv-mt/mt-list-tagcloud'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import SectionWrapper from '@adv-mt/mt-shared-section-wrapper'
import TechnicalDetailsPanelLinks from '@adv-mt/mt-technical-details-panel-links'
import TechnicalDetailsPanelSlider from '@adv-mt/mt-technical-details-panel-slider'
import MotorTrackingCriteo from '@adv-mt/mt-tracking-criteo'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {getInitialProps} from './getInitialProps.js'

const BASE_CLASS = 'mt-TechnicalDetailsVersion'
const MODEL_DETAILS_CLASS = `${BASE_CLASS}-modelDetails`
const MODEL_DETAILS_INFO_CLASS = `${MODEL_DETAILS_CLASS}-info`

const TechnicalDetailsVersion = ({
  bodyType,
  breadcrumbs,
  doors,
  helmet = {},
  makeId,
  makeName,
  makeUrlParam,
  modelId,
  modelName,
  modelUrlParam,
  pageInfoResponse,
  pageName,
  serverErrors,
  tagclouds = [],
  version,
  versionId,
  year
}) => {
  const {i18n} = useContext(Context)

  const make = {id: makeId, name: makeName}

  const hasCriticalServerErrors = Boolean(serverErrors)

  const jatoDescription = i18n.t('TECHNICAL_DETAILS_JATO_DESCRIPTION')

  const listMarketedVersionsTitle = i18n.t('TECHNICAL_DETAILS_MARKETED_VERSIONS_TITLE', {
    make: makeName,
    model: modelName,
    version: pageInfoResponse?.specifications?.version,
    hp: pageInfoResponse?.specifications?.hp
  })

  const panelSliderTitle = {
    literal: 'TECHNICAL_DETAILS_VERSION_TITLE',
    params: {
      makeName,
      modelName,
      versionName: pageInfoResponse?.specifications?.version,
      hp: pageInfoResponse?.specifications?.hp,
      year
    }
  }

  const linksTitle = i18n.t('TECHNICAL_DETAILS_MODEL_LINKS_TITLE', {
    makeName,
    modelName
  })

  const modelDetailsTitle = i18n.t('TECHNICAL_DETAILS_MODEL_DETAILS_TITLE', {
    year
  })

  const panelSliderPriceDescription = {
    literal: 'TECHNICAL_DETAILS_VERSION_PRICE_DESCRIPTION',
    params: {
      price: {
        isCurrency: true,
        currencyValue: pageInfoResponse?.price
      }
    }
  }

  return !hasCriticalServerErrors && Boolean(pageInfoResponse) ? (
    <Layout.Basic linkFactory={LinkFactory}>
      <TechnicalDetailsPanelSlider
        title={panelSliderTitle}
        description={panelSliderPriceDescription}
        images={pageInfoResponse.images}
      />
      <SectionWrapper title={modelDetailsTitle}>
        <ListModelDetails modelDetails={pageInfoResponse.specifications} />
        <p className={MODEL_DETAILS_INFO_CLASS}>{jatoDescription}</p>
      </SectionWrapper>
      <SectionWrapper title={listMarketedVersionsTitle}>
        <ListMarketedVersions marketedVersions={pageInfoResponse.marketedVersions} linkFactory={LinkFactory} />
      </SectionWrapper>
      <SectionWrapper title={linksTitle}>
        <TechnicalDetailsPanelLinks
          makeUrlParam={makeUrlParam}
          makeName={makeName}
          modelUrlParam={modelUrlParam}
          modelName={modelName}
        />
      </SectionWrapper>
      <SectionWrapper>
        <MotorTagCloud initialTagclouds={tagclouds} pageName={pageName} makeId={makeId} modelId={modelId} />
      </SectionWrapper>
      <Breadcrumb items={breadcrumbs} linkFactory={LinkFactory} />
      <Helmet {...helmet} />
      <TrackingPageView
        pageName={pageName}
        make={makeName?.toLowerCase()}
        model={modelName?.toLowerCase()}
        version={pageInfoResponse.versionName?.toLowerCase()}
      />
      <AdDisplay pageName={pageName} make={make} />
      <MotorTrackingCriteo />
    </Layout.Basic>
  ) : (
    <></>
  )
}

TechnicalDetailsVersion.displayName = 'TechnicalDetailsVersion'

TechnicalDetailsVersion.propTypes = {
  bodyType: PropTypes.string,
  breadcrumbs: PropTypes.array,
  doors: PropTypes.number,
  helmet: PropTypes.object,
  make: PropTypes.string,
  makeId: PropTypes.string,
  makeName: PropTypes.string,
  model: PropTypes.string,
  modelId: PropTypes.string,
  modelName: PropTypes.string,
  pageInfoResponse: PropTypes.object,
  pageName: PropTypes.string,
  serverErrors: PropTypes.array,
  tagclouds: PropTypes.array,
  version: PropTypes.string,
  versionId: PropTypes.string,
  year: PropTypes.number
}

TechnicalDetailsVersion.getInitialProps = getInitialProps

export default TechnicalDetailsVersion
